import { apiClient } from '../lib/apiClient';
import { getAiLimitErrorKind } from './aiUsageService';

/**
 * Frontend client for the Reef Intelligence card (#1384).
 *
 * Types mirror the backend contract exactly:
 *   - src/types/aiInsight.ts          (AquariumInsight, InsightRecommendation, …)
 *   - src/controllers/insight.controller.ts
 *       GET  /api/v1/aquariums/:aquariumId/insights          → { data: { insight, fromCache } }
 *       POST /api/v1/aquariums/:aquariumId/insights/refresh  → same, 429 RATE_LIMITED within 5 min
 *       503 SERVICE_UNAVAILABLE when no AI provider is configured/reachable
 *   - src/types/aiSuggestion.ts (AiSuggestion, GenerateSuggestionsResult)
 *       GET  /api/v1/aquariums/:aquariumId/ai-suggestions?status=suggested → { data: AiSuggestion[] }
 *       POST /api/v1/aquariums/:aquariumId/ai-suggestions/generate        → { data: { suggestions, generatedAt } }
 *
 * GET /insights never writes suggestions or tasks; suggestions are only
 * materialised by the explicit `generate` call, and tasks only by accept /
 * edit-accept (see services/aiSuggestionService.ts).
 */

export type InsightConfidence = 'low' | 'medium' | 'high';

export interface InsightRecommendation {
  readonly element: string;
  readonly productType: string;
  readonly amount: number | null;
  readonly unit: string | null;
  readonly frequency: string;
  readonly calculationBasis: string;
  readonly confidence: InsightConfidence;
  readonly rationale: string;
  readonly unknownConcentration: boolean;
  readonly advisoryOnly: boolean;
  readonly capped: boolean;
  /** Trace elements (#1829): amount withheld until a second ICP or a hobby test confirms the value. */
  readonly needsConfirmation?: boolean;
  /** Trace elements (#1829): a one-time raise spread over days (UNVERIFIED daily maximum). */
  readonly correctionPlan?: TraceCorrectionPlan | null;
}

export type InsightTrendDirection = 'rising' | 'falling' | 'stable' | 'insufficient_data';
export type InsightValueStatus = 'in_range' | 'low' | 'high' | 'unknown';

export interface InsightTrend {
  readonly elementName: string;
  readonly latestValue: number;
  readonly unit: string;
  readonly status: InsightValueStatus;
  readonly direction: InsightTrendDirection;
  readonly magnitude: number | null;
  readonly sampleSize: number;
}

export interface InsightDeviation {
  readonly elementName: string;
  readonly value: number;
  readonly unit: string;
  readonly group: 'major' | 'minor' | 'nutrients';
  readonly description: string;
}

export interface AquariumInsight {
  readonly aquariumId: string;
  readonly generatedAt: string;
  readonly provider: string;
  readonly model: string;
  readonly trends: InsightTrend[];
  readonly deviations: InsightDeviation[];
  readonly recommendations: InsightRecommendation[];
  readonly summary: string;
  /** Date of the newest ICP analysis or measurement the insight is based on (ISO); absent on older insights. */
  readonly dataAsOf?: string | null;
}

export interface InsightResponse {
  readonly insight: AquariumInsight;
  readonly fromCache: boolean;
  /** ai_insights row id, used to rate the insight (POST /api/v1/ai-feedback); null when not stored. */
  readonly insightId?: string | null;
  /**
   * Language the insight text is written in (en, de, …). A cached insight keeps
   * its language when the UI language changes; "Refresh" regenerates it.
   */
  readonly language?: string;
  /** Newer data exists but no new insight could be generated yet: this is the last saved one. */
  readonly stale?: boolean;
}

/** An AI suggestion exactly as the ai-suggestions API returns it (backend `AiSuggestion`). */
export interface ReefSuggestion {
  readonly id: string;
  readonly aquariumId: string;
  readonly suggestedTitle: string;
  readonly suggestedDescription: string | null;
  readonly suggestedPriority: string | null;
  readonly suggestedCategory: string | null;
  readonly suggestedDueDate: string | null;
  readonly status: string;
  readonly aiReasoning: string | null;
  readonly createdAt: string;
  readonly reviewedAt: string | null;
  readonly sourceType: string | null;
  readonly element: string | null;
  readonly productType: string | null;
  readonly suggestedAmount: number | null;
  readonly suggestedUnit: string | null;
  readonly suggestedFrequency: string | null;
  readonly calculationBasis: string | null;
  readonly confidence: string | null;
  readonly unknownConcentration: boolean;
  readonly advisoryOnly: boolean;
  readonly acceptedTaskId: string | null;
}

export interface GenerateSuggestionsResult {
  readonly suggestions: ReefSuggestion[];
  readonly generatedAt: string;
}

/** `sourceType` of a suggestion materialised from an insight (backend INSIGHT_SUGGESTION_SOURCE). */
export const INSIGHT_SUGGESTION_SOURCE = 'ai_insight';

/** Elements with a hard-enforced per-day limit (backend ENFORCED_LIMIT_ELEMENTS). */
export const ENFORCED_LIMIT_ELEMENTS: readonly string[] = ['alkalinity', 'calcium'];

export type LimitKind = 'enforced' | 'advisory' | 'none';

/**
 * How a recommendation's dose is bounded: alkalinity/calcium carry an enforced
 * per-day limit, advisory-only elements (magnesium/nitrate/phosphate) none, and
 * any other element is neither — it must not be labelled "enforced".
 */
export interface TraceCorrectionPlan {
  readonly totalRaise: number;
  readonly maxRaisePerDay: number;
  readonly days: number;
  readonly unit: string;
}

export function limitKind(element: string | null, advisoryOnly: boolean): LimitKind {
  if (advisoryOnly) return 'advisory';
  if (element !== null && ENFORCED_LIMIT_ELEMENTS.includes(element.trim().toLowerCase())) return 'enforced';
  return 'none';
}

/** Server-side bound for an edited dose: at most this multiple of the suggested amount (aiSuggestionService.MAX_EDITED_AMOUNT_MULTIPLIER). */
export const MAX_EDITED_AMOUNT_MULTIPLIER = 10;

function insightUrl(aquariumId: string): string {
  return `/api/v1/aquariums/${encodeURIComponent(aquariumId)}/insights`;
}

function suggestionsUrl(aquariumId: string): string {
  return `/api/v1/aquariums/${encodeURIComponent(aquariumId)}/ai-suggestions`;
}

/** Thrown when the insight endpoint answers 2xx with a body that is not the documented contract. */
export class InsightContractError extends Error {
  constructor() {
    super('Unexpected insight response shape');
    this.name = 'InsightContractError';
  }
}

/** Minimal structural check of `{ insight, fromCache }` so a bad payload renders the error state instead of crashing. */
function assertInsightResponse(value: unknown): InsightResponse {
  const insight = typeof value === 'object' && value !== null ? (value as { insight?: unknown }).insight : undefined;
  if (
    typeof insight !== 'object' ||
    insight === null ||
    !Array.isArray((insight as { trends?: unknown }).trends) ||
    !Array.isArray((insight as { recommendations?: unknown }).recommendations)
  ) {
    throw new InsightContractError();
  }
  return value as InsightResponse;
}

export async function getAquariumInsight(aquariumId: string): Promise<InsightResponse> {
  const response = await apiClient.get<{ data: unknown }>(insightUrl(aquariumId));
  return assertInsightResponse(response.data);
}

export async function refreshAquariumInsight(aquariumId: string): Promise<InsightResponse> {
  const response = await apiClient.post<{ data: unknown }>(`${insightUrl(aquariumId)}/refresh`, {});
  return assertInsightResponse(response.data);
}

/** Open (status `suggested`) suggestions that were materialised from the aquarium's insight. */
export async function listOpenReefSuggestions(aquariumId: string): Promise<ReefSuggestion[]> {
  const response = await apiClient.get<{ data: ReefSuggestion[] }>(`${suggestionsUrl(aquariumId)}?status=suggested`);
  return response.data.filter((s) => s.sourceType === INSIGHT_SUGGESTION_SOURCE);
}

export async function generateReefSuggestions(aquariumId: string): Promise<GenerateSuggestionsResult> {
  const response = await apiClient.post<{ data: GenerateSuggestionsResult }>(`${suggestionsUrl(aquariumId)}/generate`, {});
  return response.data;
}

/** Error kinds the card renders differently. */
export type InsightErrorKind = 'aiBudgetExceeded' | 'aiUnavailable' | 'rateLimited' | 'notConfigured' | 'conflict' | 'generic';

export function classifyInsightError(error: unknown): InsightErrorKind {
  // 429 AI_BUDGET_EXCEEDED / 503 AI_TEMPORARILY_UNAVAILABLE (monthly AI credits)
  const aiLimit = getAiLimitErrorKind(error);
  if (aiLimit === 'exceeded') return 'aiBudgetExceeded';
  if (aiLimit === 'unavailable') return 'aiUnavailable';
  const status = typeof error === 'object' && error !== null && 'status' in error
    ? (error as { status: unknown }).status
    : undefined;
  if (status === 429) return 'rateLimited';
  if (status === 503) return 'notConfigured';
  if (status === 409) return 'conflict';
  return 'generic';
}
